import { useCallback, useEffect, useState } from "react";
import {
  CalendarCheck,
  CheckCircle,
  ChatCircleText,
  Clock,
  Envelope,
  ListChecks,
  PaperPlaneTilt,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  completeAdmissionTask,
  completeAdmissionTour,
  createAdmissionTask,
  getAdmissionWorkspace,
  listAdmissionClassrooms,
  listAdmissionLocations,
  logAdmissionCommunication,
  replaceAdmissionSchedulePreferences,
  scheduleAdmissionTour,
  updateAdmissionDetails,
} from "@/lib/admissions";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function localDate() {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return "No due date";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "No due date" : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function initialWindow() {
  return { weekday: "0", starts_at: "08:30", ends_at: "15:30", effective_on: localDate(), ends_on: "" };
}

export default function AdmissionWorkflowPanel({ enrollment, onChanged }) {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [tourAt, setTourAt] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueOn, setTaskDueOn] = useState("");
  const [communication, setCommunication] = useState({ channel: "phone", direction: "outbound", subject: "", body: "" });
  const [scheduleDraft, setScheduleDraft] = useState([]);
  const [newWindow, setNewWindow] = useState(initialWindow);
  const [locations, setLocations] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [details, setDetails] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await getAdmissionWorkspace(enrollment.id);
      setWorkspace(next);
      setScheduleDraft(next.schedulePreferences || []);
    } catch (err) {
      toast.error(err?.message || "Could not load admission operations");
    } finally {
      setLoading(false);
    }
  }, [enrollment.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setDetails({
      location_id: enrollment.location_id || "",
      preferred_classroom_id: enrollment.preferred_classroom_id || "",
      preferred_start_date: enrollment.preferred_start_date || "",
      desired_program: enrollment.desired_program || "",
      referral_name: enrollment.referral_name || "",
      waitlist_priority: enrollment.waitlist_priority ?? "",
    });
  }, [
    enrollment.id,
    enrollment.location_id,
    enrollment.preferred_classroom_id,
    enrollment.preferred_start_date,
    enrollment.desired_program,
    enrollment.referral_name,
    enrollment.waitlist_priority,
  ]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [nextLocations, nextClassrooms] = await Promise.all([
          listAdmissionLocations(),
          listAdmissionClassrooms(),
        ]);
        if (!active) return;
        setLocations(nextLocations);
        setClassrooms(nextClassrooms);
      } catch (err) {
        if (active) toast.error(err?.message || "Could not load center options");
      }
    })();
    return () => { active = false; };
  }, [enrollment.id]);

  const afterMutation = async (message) => {
    await load();
    if (onChanged) await onChanged();
    toast.success(message);
  };

  const perform = async (key, action, message) => {
    setBusy(key);
    try {
      await action();
      await afterMutation(message);
    } catch (err) {
      toast.error(err?.message || "Could not save admissions work");
    } finally {
      setBusy("");
    }
  };

  const onScheduleTour = (event) => {
    event.preventDefault();
    if (!tourAt) return;
    perform("tour", async () => {
      await scheduleAdmissionTour({
        enrollmentId: enrollment.id,
        scheduledAt: new Date(tourAt).toISOString(),
        locationId: enrollment.location_id || null,
      });
      setTourAt("");
    }, "Tour scheduled. A confirmation will be queued when the family has an email on file.");
  };

  const onAddTask = (event) => {
    event.preventDefault();
    if (!taskTitle.trim()) return;
    perform("task", async () => {
      await createAdmissionTask({ enrollmentId: enrollment.id, title: taskTitle.trim(), dueOn: taskDueOn || null });
      setTaskTitle("");
      setTaskDueOn("");
    }, "Task added.");
  };

  const onLogCommunication = (event) => {
    event.preventDefault();
    if (!communication.subject.trim() && !communication.body.trim()) return;
    perform("communication", async () => {
      await logAdmissionCommunication({
        enrollmentId: enrollment.id,
        channel: communication.channel,
        direction: communication.direction,
        subject: communication.subject.trim() || null,
        body: communication.body.trim() || null,
      });
      setCommunication({ channel: "phone", direction: "outbound", subject: "", body: "" });
    }, "Contact activity logged.");
  };

  const onSaveDetails = (event) => {
    event.preventDefault();
    perform("details", async () => {
      await updateAdmissionDetails({
        enrollmentId: enrollment.id,
        expectedUpdatedAt: enrollment.updated_at,
        patch: {
          location_id: details.location_id || "",
          preferred_classroom_id: details.preferred_classroom_id || "",
          preferred_start_date: details.preferred_start_date || "",
          desired_program: details.desired_program?.trim() || "",
          referral_name: details.referral_name?.trim() || "",
          waitlist_priority: details.waitlist_priority === "" ? null : Number(details.waitlist_priority),
        },
      });
    }, "Planning details saved.");
  };

  const addScheduleWindow = () => {
    if (!newWindow.starts_at || !newWindow.ends_at || newWindow.ends_at <= newWindow.starts_at) {
      toast.error("Choose an end time after the start time.");
      return;
    }
    const window = {
      weekday: Number(newWindow.weekday),
      starts_at: newWindow.starts_at,
      ends_at: newWindow.ends_at,
      effective_on: newWindow.effective_on || localDate(),
      ends_on: newWindow.ends_on || null,
    };
    setScheduleDraft((current) => [...current, window].sort((a, b) => a.weekday - b.weekday || a.starts_at.localeCompare(b.starts_at)));
    setNewWindow(initialWindow());
  };

  const saveSchedule = () => perform("schedule", async () => {
    await replaceAdmissionSchedulePreferences({
      enrollmentId: enrollment.id,
      windows: scheduleDraft.map((window) => ({
        weekday: Number(window.weekday),
        starts_at: window.starts_at,
        ends_at: window.ends_at,
        effective_on: window.effective_on,
        ends_on: window.ends_on || null,
      })),
    });
  }, "Schedule preference saved.");

  if (loading && !workspace) {
    return <section data-testid="admission-workflow-loading" className="h-72 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-50" aria-label="Loading admission operations" />;
  }

  const tours = workspace?.tours || [];
  const tasks = workspace?.tasks || [];
  const communications = workspace?.communications || [];
  const jobs = workspace?.notificationJobs || [];
  const availableClassrooms = details.location_id
    ? classrooms.filter((classroom) => classroom.location_id === details.location_id)
    : classrooms;

  return (
    <section data-testid="admission-workflow-panel" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-hover">Admission operations</p>
          <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight text-neutral-900">Move the case forward</h2>
          <p className="mt-1 max-w-2xl text-sm text-neutral-600">Schedule and complete real work here. The record remains the source of truth, and every change is tenant-scoped and audited.</p>
        </div>
        <button type="button" onClick={load} disabled={Boolean(busy)} className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-50">Refresh</button>
      </div>

      <SectionCard icon={ListChecks} title="Planning & placement" subtitle="Center configuration guides operations; it does not reserve a spot or extend an offer.">
        <form onSubmit={onSaveDetails} className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label className="text-xs font-semibold text-neutral-600">Center
            <select data-testid="admission-detail-location" value={details.location_id || ""} onChange={(event) => setDetails((current) => ({ ...current, location_id: event.target.value, preferred_classroom_id: classrooms.find((room) => room.id === current.preferred_classroom_id)?.location_id === event.target.value ? current.preferred_classroom_id : "" }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">
              <option value="">Not assigned yet</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-neutral-600">Preferred classroom
            <select data-testid="admission-detail-classroom" value={details.preferred_classroom_id || ""} onChange={(event) => setDetails((current) => ({ ...current, preferred_classroom_id: event.target.value }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">
              <option value="">No classroom preference</option>{availableClassrooms.map((classroom) => <option key={classroom.id} value={classroom.id}>{classroom.name}{classroom.age_group ? ` · ${classroom.age_group}` : ""}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-neutral-600">Preferred start
            <input data-testid="admission-detail-start" type="date" value={details.preferred_start_date || ""} onChange={(event) => setDetails((current) => ({ ...current, preferred_start_date: event.target.value }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </label>
          <label className="text-xs font-semibold text-neutral-600">Desired program
            <input data-testid="admission-detail-program" value={details.desired_program || ""} onChange={(event) => setDetails((current) => ({ ...current, desired_program: event.target.value }))} maxLength={200} placeholder="e.g. Full-day toddler" className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </label>
          <label className="text-xs font-semibold text-neutral-600">Referral source
            <input value={details.referral_name || ""} onChange={(event) => setDetails((current) => ({ ...current, referral_name: event.target.value }))} maxLength={250} placeholder="Optional name or organization" className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </label>
          <label className="text-xs font-semibold text-neutral-600">Center queue priority
            <input data-testid="admission-detail-priority" type="number" value={details.waitlist_priority ?? ""} onChange={(event) => setDetails((current) => ({ ...current, waitlist_priority: event.target.value }))} placeholder="0" className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </label>
          <div className="flex items-end"><button data-testid="admission-detail-save" disabled={busy === "details"} className="min-h-10 rounded-full bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50">{busy === "details" ? "Saving…" : "Save planning details"}</button></div>
        </form>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard icon={CalendarCheck} title="Tours" subtitle="A scheduled tour advances a lead to the tour stage and queues the appropriate confirmation.">
          <form onSubmit={onScheduleTour} className="flex flex-col gap-2 sm:flex-row">
            <input data-testid="admission-tour-at" type="datetime-local" value={tourAt} onChange={(event) => setTourAt(event.target.value)} className="min-h-10 min-w-0 flex-1 rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required />
            <button data-testid="admission-tour-schedule" disabled={busy === "tour"} className="min-h-10 rounded-full bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50">{busy === "tour" ? "Scheduling…" : "Schedule tour"}</button>
          </form>
          <ul className="mt-4 space-y-2">
            {tours.length === 0 ? <Empty text="No tours are recorded yet." /> : tours.map((tour) => (
              <li key={tour.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-100 bg-neutral-50 px-3 py-2.5">
                <div><p className="text-sm font-semibold text-neutral-900">{formatDateTime(tour.scheduled_at)}</p><p className="mt-0.5 text-xs capitalize text-neutral-500">{tour.status.replaceAll("_", " ")}</p></div>
                {tour.status === "scheduled" && <div className="flex gap-2"><button type="button" onClick={() => perform(`tour-${tour.id}`, () => completeAdmissionTour({ tourId: tour.id, status: "completed" }), "Tour completed.")} disabled={Boolean(busy)} className="rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary-hover disabled:opacity-50">Complete</button><button type="button" onClick={() => perform(`tour-${tour.id}`, () => completeAdmissionTour({ tourId: tour.id, status: "no_show" }), "Tour marked as no-show.")} disabled={Boolean(busy)} className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 disabled:opacity-50">No show</button></div>}
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={ListChecks} title="Follow-up tasks" subtitle="Small next actions keep the team out of a spreadsheet.">
          <form onSubmit={onAddTask} className="grid gap-2 sm:grid-cols-[1fr_9rem_auto]">
            <input data-testid="admission-task-title" value={taskTitle} onChange={(event) => setTaskTitle(event.target.value)} placeholder="e.g. Verify start date" maxLength={250} className="min-h-10 rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required />
            <input data-testid="admission-task-due" type="date" value={taskDueOn} onChange={(event) => setTaskDueOn(event.target.value)} className="min-h-10 rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            <button data-testid="admission-task-add" disabled={busy === "task"} className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50"><Plus size={14} weight="bold" /> Add</button>
          </form>
          <ul className="mt-4 space-y-2">
            {tasks.length === 0 ? <Empty text="No follow-up tasks yet." /> : tasks.map((task) => <li key={task.id} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-neutral-50 px-3 py-2.5"><button type="button" aria-label={`${task.status === "completed" ? "Reopen" : "Complete"} ${task.title}`} onClick={() => perform(`task-${task.id}`, () => completeAdmissionTask({ taskId: task.id, complete: task.status !== "completed" }), task.status === "completed" ? "Task reopened." : "Task completed.")} disabled={Boolean(busy)} className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${task.status === "completed" ? "border-primary bg-primary text-white" : "border-neutral-300 bg-white"}`}><CheckCircle size={13} weight="fill" className={task.status === "completed" ? "" : "invisible"} /></button><span className="min-w-0 flex-1"><span className={`block truncate text-sm ${task.status === "completed" ? "text-neutral-400 line-through" : "font-medium text-neutral-900"}`}>{task.title}</span><span className="text-xs text-neutral-500">{formatDate(task.due_on)}</span></span></li>)}
          </ul>
        </SectionCard>

        <SectionCard icon={Clock} title="Prospective schedule" subtitle="Capture what the family needs once; conversion carries it into the child schedule only when a classroom is selected.">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs font-semibold text-neutral-600">Day<select value={newWindow.weekday} onChange={(event) => setNewWindow((current) => ({ ...current, weekday: event.target.value }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">{WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}</select></label>
            <label className="text-xs font-semibold text-neutral-600">Effective date<input type="date" value={newWindow.effective_on} onChange={(event) => setNewWindow((current) => ({ ...current, effective_on: event.target.value }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <label className="text-xs font-semibold text-neutral-600">Start<input type="time" value={newWindow.starts_at} onChange={(event) => setNewWindow((current) => ({ ...current, starts_at: event.target.value }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <label className="text-xs font-semibold text-neutral-600">End<input type="time" value={newWindow.ends_at} onChange={(event) => setNewWindow((current) => ({ ...current, ends_at: event.target.value }))} className="mt-1 min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2"><button type="button" onClick={addScheduleWindow} className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50"><Plus size={13} weight="bold" /> Add window</button><button type="button" onClick={saveSchedule} disabled={busy === "schedule"} className="min-h-9 rounded-full bg-primary px-3 text-xs font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50">{busy === "schedule" ? "Saving…" : "Save schedule"}</button></div>
          <ul className="mt-4 space-y-2">{scheduleDraft.length === 0 ? <Empty text="No schedule preference captured yet." /> : scheduleDraft.map((window, index) => <li key={window.id || `${window.weekday}-${window.starts_at}-${index}`} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-100 bg-neutral-50 px-3 py-2"><span className="text-sm text-neutral-800"><strong>{WEEKDAYS[Number(window.weekday)]}</strong> · {window.starts_at.slice(0, 5)}–{window.ends_at.slice(0, 5)}</span><button type="button" aria-label="Remove schedule window" onClick={() => setScheduleDraft((current) => current.filter((_, currentIndex) => currentIndex !== index))} className="rounded-lg p-1.5 text-neutral-400 hover:bg-white hover:text-[#8A5A12]"><Trash size={14} weight="bold" /></button></li>)}</ul>
        </SectionCard>

        <SectionCard icon={ChatCircleText} title="Contact history" subtitle="Log meaningful communication so the next person has context.">
          <form onSubmit={onLogCommunication} className="space-y-2">
            <div className="grid gap-2 sm:grid-cols-2"><select value={communication.channel} onChange={(event) => setCommunication((current) => ({ ...current, channel: event.target.value }))} className="min-h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"><option value="phone">Phone</option><option value="email">Email</option><option value="sms">SMS</option><option value="in_person">In person</option><option value="internal_note">Internal note</option></select><select value={communication.direction} onChange={(event) => setCommunication((current) => ({ ...current, direction: event.target.value }))} className="min-h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"><option value="outbound">Outbound</option><option value="inbound">Inbound</option><option value="internal">Internal</option></select></div>
            <input data-testid="admission-communication-subject" value={communication.subject} onChange={(event) => setCommunication((current) => ({ ...current, subject: event.target.value }))} placeholder="Subject (optional)" maxLength={300} className="min-h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            <textarea data-testid="admission-communication-body" value={communication.body} onChange={(event) => setCommunication((current) => ({ ...current, body: event.target.value }))} placeholder="What was discussed?" rows={3} maxLength={4000} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            <button data-testid="admission-communication-submit" disabled={busy === "communication" || (!communication.subject.trim() && !communication.body.trim())} className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50"><PaperPlaneTilt size={14} weight="bold" /> {busy === "communication" ? "Saving…" : "Log activity"}</button>
          </form>
          <ul className="mt-4 space-y-2">{communications.length === 0 ? <Empty text="No contact history yet." /> : communications.slice(0, 4).map((item) => <li key={item.id} className="rounded-xl border border-neutral-100 bg-neutral-50 px-3 py-2.5"><div className="flex items-center justify-between gap-3"><p className="text-sm font-semibold capitalize text-neutral-900">{item.subject || item.channel.replaceAll("_", " ")}</p><span className="text-[11px] text-neutral-500">{formatDateTime(item.occurred_at)}</span></div>{item.body && <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-neutral-600">{item.body}</p>}</li>)}</ul>
        </SectionCard>
      </div>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5" aria-labelledby="admission-outbox-title">
        <div className="flex items-start gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover"><Envelope size={17} weight="duotone" /></span><div><p id="admission-outbox-title" className="font-heading text-base font-semibold text-neutral-900">Notification outbox</p><p className="mt-1 text-sm text-neutral-600">Transactional updates are queued by approved workflow changes and sent in the background. This view never sends an unreviewed message directly.</p></div></div>
        <div className="mt-4 flex flex-wrap gap-2">{jobs.length === 0 ? <Empty text="No transactional messages are queued for this case." /> : jobs.map((job) => <span key={job.id} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${job.status === "sent" ? "border-primary/30 bg-primary/5 text-primary-hover" : job.status === "failed" ? "border-[#C6851E]/30 bg-[#C6851E]/10 text-[#8A5A12]" : "border-neutral-200 bg-neutral-50 text-neutral-700"}`}><span className="capitalize">{job.kind.replaceAll("_", " ")}</span><span className="text-neutral-400">·</span><span className="capitalize">{job.status}</span></span>)}</div>
      </section>
    </section>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return <section className="rounded-2xl border border-neutral-200 bg-white p-5"><div className="mb-4 flex items-start gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover"><Icon size={17} weight="duotone" /></span><div><h3 className="font-heading text-base font-semibold text-neutral-900">{title}</h3><p className="mt-1 text-xs leading-relaxed text-neutral-500">{subtitle}</p></div></div>{children}</section>;
}

function Empty({ text }) {
  return <li className="rounded-xl border border-dashed border-neutral-200 px-3 py-3 text-center text-xs text-neutral-500">{text}</li>;
}
